<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import BoardTile from './BoardTile.vue'
import BoardPointsModal from './BoardPointsModal.vue'
import BoardChartModal from './BoardChartModal.vue'

const POLL_MS = 60000
const ALL_TIMEFRAMES = ['1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '1d']

type PointRow = {
  id: number
  side: string
  side_label: string
  price: number
  status: string
  status_label: string
  mtf_label?: string | null
  url: string
}

type BoardTileRow = {
  symbol: string
  label: string
  last_price: number | null
  zones: any
  error: string | null
  longs: PointRow[]
  shorts: PointRow[]
}

const props = defineProps<{
  exchange: string
  refreshMs: number
  apiBase: string
}>()

const emit = defineEmits<{
  fullscreen: [value: boolean]
}>()

const apiBase = (props.apiBase || '/api/desk').replace(/\/$/, '')
const exchange = props.exchange || 'Binance'

const timeframe = ref('1h')
const threshold = ref(0.85)
const loading = ref(false)
const errorMessage = ref('')
const tiles = ref<BoardTileRow[]>([])
const fullscreen = ref(false)
const viewport = ref({ width: 1280, height: 800 })
const zoneBusy = ref<string | null>(null)
const pointsTile = ref<BoardTileRow | null>(null)
const chartTile = ref<BoardTileRow | null>(null)
const dragging = ref(false)
const dragFrom = ref<number | null>(null)
const dragOver = ref<number | null>(null)
const gridEl = ref<HTMLElement | null>(null)
let loadSeq = 0
let poller: ReturnType<typeof setInterval> | undefined
let dragArmed = false
let dragPointerId: number | null = null
let dragStart = { x: 0, y: 0 }

const orderKey = `pointra.desk.board.order.${exchange}`

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

async function fetchJson(url: string, init: RequestInit = {}) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers || {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const err: any = new Error(body.message || body.detail || res.statusText)
    err.status = res.status
    throw err
  }
  return res.json()
}

function pageIsVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible'
}

function readQuery() {
  const params = new URLSearchParams(window.location.search)
  const tf = params.get('timeframe')
  if (tf && ALL_TIMEFRAMES.includes(tf)) timeframe.value = tf
}

function writeQuery() {
  const params = new URLSearchParams(window.location.search)
  params.set('timeframe', timeframe.value)
  const qs = params.toString()
  const next = `${window.location.pathname}?${qs}`
  const current = `${window.location.pathname}${window.location.search}`
  if (current !== next) {
    window.history.replaceState(window.history.state, '', next)
  }
}

function measureViewport() {
  viewport.value = {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

const columns = computed(() => {
  const count = Math.max(1, tiles.value.length)
  const width = viewport.value.width
  const fs = fullscreen.value
  if (count <= 1 || width < 720) return 1
  if (!fs) return 2
  if (width < 1100) return 2
  if (width < 1480) return Math.min(3, count)
  if (count <= 6) return 3
  if (count <= 9) return 3
  return Math.min(5, count)
})

const narrow = computed(() => viewport.value.width < 720)

const rows = computed(() => Math.max(1, Math.ceil(Math.max(tiles.value.length, 1) / columns.value)))

const chartHeight = computed(() => {
  const fs = fullscreen.value
  const height = viewport.value.height
  const width = viewport.value.width
  if (fs) {
    if (width < 720) {
      return Math.max(260, Math.round(height * 0.52))
    }
    const chrome = 52
    const gaps = 8 * (rows.value + 1)
    const head = 36
    const fitted = Math.floor((height - chrome - gaps) / rows.value) - head
    if (fitted < 220) {
      return Math.max(240, Math.round(height * 0.38))
    }
    return fitted
  }
  return width < 720 ? 320 : 540
})

function readOrder(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(orderKey) || '[]')
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : []
  } catch {
    return []
  }
}

function persistOrder() {
  try {
    localStorage.setItem(orderKey, JSON.stringify(tiles.value.map((tile) => tile.symbol)))
  } catch {
    /* ignore quota / private mode */
  }
}

function applyOrder(list: BoardTileRow[]): BoardTileRow[] {
  const order = readOrder()
  if (!order.length) return list
  const map = new Map(list.map((tile) => [tile.symbol, tile]))
  const sorted: BoardTileRow[] = []
  for (const symbol of order) {
    const tile = map.get(symbol)
    if (tile) {
      sorted.push(tile)
      map.delete(symbol)
    }
  }
  for (const tile of list) {
    if (map.has(tile.symbol)) sorted.push(tile)
  }
  return sorted
}

function stopDrag() {
  window.removeEventListener('pointermove', onDragMove)
  window.removeEventListener('pointerup', onDragEnd)
  window.removeEventListener('pointercancel', onDragEnd)
  document.body.classList.remove('board-dragging')
  dragging.value = false
  dragFrom.value = null
  dragOver.value = null
  dragArmed = false
  dragPointerId = null
}

function indexFromPoint(x: number, y: number): number | null {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-board-index]'))
  let best: number | null = null
  let bestDist = Infinity
  for (const node of nodes) {
    const index = Number(node.dataset.boardIndex)
    if (Number.isNaN(index)) continue
    const rect = node.getBoundingClientRect()
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
      return index
    }
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    const dist = Math.hypot(x - cx, y - cy)
    if (dist < bestDist) {
      bestDist = dist
      best = index
    }
  }
  return best
}

function onGrip(event: PointerEvent, index: number) {
  if (event.button != null && event.button !== 0) return
  event.preventDefault()
  dragFrom.value = index
  dragOver.value = index
  dragArmed = false
  dragPointerId = event.pointerId
  dragStart = { x: event.clientX, y: event.clientY }
  window.addEventListener('pointermove', onDragMove)
  window.addEventListener('pointerup', onDragEnd)
  window.addEventListener('pointercancel', onDragEnd)
}

function autoScroll(y: number) {
  const grid = gridEl.value
  if (grid && grid.scrollHeight > grid.clientHeight + 8) {
    const rect = grid.getBoundingClientRect()
    if (y < rect.top + 56) grid.scrollTop -= 28
    else if (y > rect.bottom - 56) grid.scrollTop += 28
    return
  }
  if (y < 56) window.scrollBy(0, -28)
  else if (y > window.innerHeight - 56) window.scrollBy(0, 28)
}

function onDragMove(event: PointerEvent) {
  if (dragFrom.value == null) return
  if (dragPointerId != null && event.pointerId !== dragPointerId) return
  const dist = Math.hypot(event.clientX - dragStart.x, event.clientY - dragStart.y)
  if (!dragArmed) {
    if (dist < 8) return
    dragArmed = true
    dragging.value = true
    document.body.classList.add('board-dragging')
  }
  autoScroll(event.clientY)
  const over = indexFromPoint(event.clientX, event.clientY)
  if (over != null) dragOver.value = over
}

function onDragEnd() {
  const from = dragFrom.value
  const over = dragOver.value
  const moved = dragArmed && from != null && over != null && from !== over
  if (moved) {
    const next = [...tiles.value]
    const [tile] = next.splice(from, 1)
    next.splice(over, 0, tile)
    tiles.value = next
    persistOrder()
  }
  stopDrag()
}

async function loadBoard(options: { silent?: boolean } = {}) {
  const { silent = false } = options
  const seq = ++loadSeq
  if (!silent) loading.value = true
  if (!silent) errorMessage.value = ''

  try {
    const data = await fetchJson(
      `${apiBase}/board?exchange=${encodeURIComponent(exchange)}&threshold=${threshold.value}`,
    )
    if (seq !== loadSeq) return
    tiles.value = applyOrder(Array.isArray(data.tiles) ? data.tiles : [])
  } catch (error: any) {
    if (seq !== loadSeq) return
    if (!silent) {
      errorMessage.value = error?.message || 'Request failed'
      tiles.value = []
    }
  } finally {
    if (seq === loadSeq && !silent) loading.value = false
  }
}

function pollBoard() {
  if (!pageIsVisible()) return
  loadBoard({ silent: true })
}

function patchTileZones(symbol: string, zones: any) {
  tiles.value = tiles.value.map((tile) => (
    tile.symbol === symbol ? { ...tile, zones, error: null } : tile
  ))
}

async function postZone(
  symbol: string,
  action: 'block' | 'unblock',
  payload: Record<string, unknown>,
) {
  if (zoneBusy.value) return
  zoneBusy.value = symbol
  errorMessage.value = ''
  try {
    const next = await fetchJson(
      `${apiBase}/${exchange}/${symbol}/zones/${action}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          ...payload,
          threshold: threshold.value,
        }),
      },
    )
    patchTileZones(symbol, next)
    await loadBoard({ silent: true })
  } catch (error: any) {
    errorMessage.value = error?.message || 'Request failed'
  } finally {
    zoneBusy.value = null
  }
}

function onBlock(symbol: string, row: { timeframe: string; side: string; price: number }) {
  return postZone(symbol, 'block', {
    timeframe: row.timeframe,
    side: row.side,
    price: row.price,
  })
}

function onUnblock(symbol: string, row: {
  block_id?: number | null
  timeframe: string
  side: string
  price: number
}) {
  return postZone(symbol, 'unblock', {
    block_id: row.block_id || undefined,
    timeframe: row.timeframe,
    side: row.side,
    price: row.price,
  })
}

function setFullscreen(value: boolean) {
  fullscreen.value = value
  emit('fullscreen', value)
  document.body.classList.toggle('board-fullscreen', value)
  requestAnimationFrame(() => {
    measureViewport()
    window.dispatchEvent(new Event('resize'))
  })
}

function onKey(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  if (pointsTile.value) {
    pointsTile.value = null
    event.preventDefault()
    return
  }
  if (chartTile.value) {
    chartTile.value = null
    event.preventDefault()
    return
  }
  if (fullscreen.value) {
    setFullscreen(false)
    event.preventDefault()
  }
}

watch(threshold, () => loadBoard())
watch(timeframe, writeQuery)
watch(tiles, (next) => {
  if (pointsTile.value) {
    const freshPoints = next.find((tile) => tile.symbol === pointsTile.value?.symbol)
    pointsTile.value = freshPoints || null
  }
  if (chartTile.value) {
    const freshChart = next.find((tile) => tile.symbol === chartTile.value?.symbol)
    chartTile.value = freshChart || null
  }
})

onMounted(async () => {
  readQuery()
  measureViewport()
  window.addEventListener('resize', measureViewport, { passive: true })
  window.addEventListener('keydown', onKey)
  await loadBoard()
  poller = setInterval(pollBoard, props.refreshMs || POLL_MS)
})

onBeforeUnmount(() => {
  clearInterval(poller)
  stopDrag()
  window.removeEventListener('resize', measureViewport)
  window.removeEventListener('keydown', onKey)
  document.body.classList.remove('board-fullscreen')
})
</script>

<template>
  <div class="board-app min-w-0">
    <Teleport to="body" :disabled="!fullscreen">
      <div class="board-shell" :class="{ 'board-shell--fs': fullscreen, 'board-shell--narrow': narrow }">
        <header class="board-toolbar panel-glass" :class="{ 'board-toolbar--fs': fullscreen }">
          <div class="panel-glass-content board-toolbar__inner">
            <div v-if="!fullscreen" class="board-toolbar__copy">
              <p class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                {{ __('Chart board') }}
              </p>
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">
                {{ exchange }} · {{ tiles.length }} {{ __('pairs') }}
              </p>
              <p class="mt-0.5 text-[11px] text-zinc-500">
                {{ __('Zones, points and candles refresh every 60s.') }}
              </p>
            </div>

            <div class="board-toolbar__tfs" role="tablist" :aria-label="__('Timeframe')">
              <button
                v-for="tf in ALL_TIMEFRAMES"
                :key="tf"
                type="button"
                class="panel-glass-btn rounded-full px-2.5 py-1.5 text-xs font-medium sm:px-3"
                :class="{ 'panel-glass-btn--active': tf === timeframe }"
                @click="timeframe = tf"
              >
                {{ tf }}
              </button>
            </div>

            <label v-if="!fullscreen" class="board-toolbar__sens">
              <span class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                {{ __('Sensitivity') }}
                <span class="ms-1 font-mono normal-case tracking-normal text-zinc-500">
                  {{ threshold.toFixed(2) }}
                </span>
              </span>
              <input
                class="zone-slider"
                type="range"
                min="0.05"
                max="0.95"
                step="0.05"
                :value="threshold"
                @input="threshold = Number(($event.target as HTMLInputElement).value)"
              >
            </label>

            <div class="board-toolbar__end">
              <button
                type="button"
                class="panel-glass-btn rounded-full px-3 py-2 text-xs font-semibold disabled:opacity-60"
                :disabled="loading"
                @click="loadBoard()"
              >
                {{ loading ? __('Loading…') : __('Refresh') }}
              </button>
              <button
                type="button"
                class="panel-glass-btn rounded-full px-3 py-2 text-xs font-semibold"
                :class="{ 'panel-glass-btn--active': fullscreen }"
                @click="setFullscreen(!fullscreen)"
              >
                {{ fullscreen ? __('Exit fullscreen') : __('Fullscreen') }}
              </button>
            </div>
          </div>
        </header>

        <p
          v-if="errorMessage"
          class="board-error panel-glass rounded-2xl border-rose-300/40 p-3 text-sm text-rose-600 dark:text-rose-300"
        >
          <span class="panel-glass-content">{{ errorMessage }}</span>
        </p>

        <p v-else-if="loading && !tiles.length" class="board-empty text-sm text-zinc-500">
          {{ __('Loading charts…') }}
        </p>

        <p v-else-if="!tiles.length" class="board-empty text-sm text-zinc-500">
          {{ __('No active coins to plot.') }}
        </p>

        <div
          v-else
          ref="gridEl"
          class="board-grid"
          :class="{ 'is-dragging': dragging }"
          :style="{ '--board-cols': String(columns) }"
        >
          <div
            v-for="(tile, index) in tiles"
            :key="tile.symbol"
            class="board-item"
            :class="{
              'is-source': dragging && dragFrom === index,
              'is-target': dragging && dragOver === index && dragFrom !== index,
            }"
            :data-board-index="index"
            :data-board-symbol="tile.symbol"
          >
            <BoardTile
              :exchange="exchange"
              :symbol="tile.symbol"
              :label="tile.label"
              :last-price="tile.last_price"
              :zones="tile.zones"
              :error="tile.error"
              :longs="tile.longs || []"
              :shorts="tile.shorts || []"
              :timeframe="timeframe"
              :api-base="apiBase"
              :chart-height="chartHeight"
              :refresh-ms="props.refreshMs || POLL_MS"
              :compact-chrome="fullscreen"
              :zone-busy="zoneBusy === tile.symbol"
              @block="onBlock(tile.symbol, $event)"
              @unblock="onUnblock(tile.symbol, $event)"
              @points="pointsTile = tile"
              @expand="chartTile = tile"
              @grip="onGrip($event, index)"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <BoardChartModal
      :open="Boolean(chartTile)"
      :exchange="exchange"
      :label="chartTile?.label || ''"
      :symbol="chartTile?.symbol || ''"
      :last-price="chartTile?.last_price"
      :zones="chartTile?.zones"
      :timeframe="timeframe"
      :api-base="apiBase"
      :refresh-ms="props.refreshMs || POLL_MS"
      :longs="chartTile?.longs || []"
      :shorts="chartTile?.shorts || []"
      @close="chartTile = null"
      @block="chartTile && onBlock(chartTile.symbol, $event)"
      @points="pointsTile = chartTile"
    />

    <BoardPointsModal
      :open="Boolean(pointsTile)"
      :label="pointsTile?.label || ''"
      :symbol="pointsTile?.symbol || ''"
      :last-price="pointsTile?.last_price"
      :longs="pointsTile?.longs || []"
      :shorts="pointsTile?.shorts || []"
      @close="pointsTile = null"
    />
  </div>
</template>

<style scoped>
.board-shell--fs {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: #0b0e14;
  overflow: hidden;
}
.board-shell--fs.board-shell--narrow {
  padding: 6px;
  gap: 6px;
}
.board-toolbar {
  border-radius: 1.5rem;
  padding: 0.75rem 1rem;
}
.board-toolbar--fs {
  border-radius: 0.75rem;
  padding: 0.4rem 0.6rem;
  background: rgba(30, 34, 45, 0.92);
  border: 1px solid #2a2e39;
  backdrop-filter: none;
}
.board-toolbar--fs::after {
  display: none;
}
.board-toolbar__inner {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  gap: 0.75rem 1rem;
}
.board-toolbar--fs .board-toolbar__inner {
  align-items: center;
}
.board-toolbar__tfs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.board-toolbar__sens {
  min-width: 10rem;
  flex: 1 1 12rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.board-toolbar__end {
  display: flex;
  gap: 0.4rem;
  margin-inline-start: auto;
}
.board-grid {
  display: grid;
  grid-template-columns: repeat(var(--board-cols, 2), minmax(0, 1fr));
  gap: 0.75rem;
  align-content: start;
}
.board-item {
  min-width: 0;
}
.board-item.is-source {
  opacity: 0.45;
}
.board-item.is-target {
  outline: 2px dashed #2962ff;
  outline-offset: 2px;
  border-radius: 1rem;
}
.board-grid.is-dragging .board-item :deep(.liq-chart) {
  pointer-events: none;
}
.board-shell--fs .board-grid {
  flex: 1;
  min-height: 0;
  gap: 6px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
.board-shell--fs.board-shell--narrow .board-toolbar__tfs {
  max-width: 100%;
}
.board-shell--fs.board-shell--narrow .board-toolbar__end {
  margin-inline-start: 0;
}
.board-empty,
.board-error {
  margin: 0.5rem 0;
}
.zone-slider {
  -webkit-appearance: none;
  appearance: none;
  direction: ltr;
  height: 0.375rem;
  border-radius: 9999px;
  background: rgba(161, 161, 170, 0.35);
  outline: none;
}
.zone-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  background: rgb(59 130 246);
  border: 2px solid rgba(255, 255, 255, 0.9);
  cursor: pointer;
}
.zone-slider::-moz-range-thumb {
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  background: rgb(59 130 246);
  border: 0;
  cursor: pointer;
}
</style>

<style>
body.board-fullscreen {
  overflow: hidden;
}
body.board-dragging {
  cursor: grabbing;
  user-select: none;
}
</style>
